import axiosInstance from '@/services/api';
import { getToken } from '@/utils/handleToken';
import React, {
	createContext,
	useContext,
	useEffect,
	useState,
	useCallback,
} from 'react';

interface Permissions {
	add: boolean;
	edit: boolean;
	delete: boolean;
	view: boolean;
}

interface PermissionContextType {
	getPermissions: (permalink: string) => Permissions;
	loading: boolean;
	permError: string | null;
}

const defaultPermissions: Permissions = {
	add: false,
	edit: false,
	delete: false,
	view: false,
};

// Fungsi untuk mengekstrak permissions yang diperlukan saja
const extractPermissions = (menuItems: any[]): Record<string, Permissions> => {
	const permissionsMap: Record<string, Permissions> = {};

	const processItem = (item: any) => {
		// Hanya simpan jika ada permalink
		if (item.permalink) {
			permissionsMap[item.permalink] = item.permissions;
		}
		// Proses child items jika ada
		if (Array.isArray(item.child)) {
			item.child.forEach(processItem);
		}
	};

	menuItems.forEach(processItem);
	return permissionsMap;
};

const PermissionContext = createContext<PermissionContextType>({
	getPermissions: () => defaultPermissions,
	loading: true,
	permError: null,
});

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [permissionsMap, setPermissionsMap] = useState<
		Record<string, Permissions>
	>({});
	const [loading, setLoading] = useState(true);
	const [permError, setPermError] = useState<string | null>(null);

	useEffect(() => {
		const fetchPermissions = async () => {
			// Cek apakah ada token, jika tidak ada skip fetch permissions
			const token = getToken();
			if (!token) {
				console.log('[PermissionContext] No token found, skipping permission fetch');
				setLoading(false);
				return;
			}

			try {
				const response = await axiosInstance.post(
					'/access/menu',
					{ req_from: process.env.NEXT_PUBLIC_NAME_REQ_FROM },
					{
						headers: {
							Authorization: token,
							'Content-Type': 'application/json',
						},
					}
				);

				if (response.data.status === 'success') {
					const extractedPermissions = extractPermissions(response.data.data);
					console.log('[PermissionContext] Permissions loaded:', extractedPermissions);
					setPermissionsMap(extractedPermissions);
				}
			} catch (err) {
				console.error('[PermissionContext] Error fetching permissions:', err);
				// Jangan set error jika token tidak ada atau invalid
				// Biarkan halaman yang membutuhkan permission yang handle
				setPermError('Gagal mengambil data permissions');
			} finally {
				setLoading(false);
			}
		};

		fetchPermissions();
	}, []);

	const getPermissions = useCallback(
		(permalink: string): Permissions => {
			return permissionsMap[permalink] || defaultPermissions;
		},
		[permissionsMap]
	);

	return (
		<PermissionContext.Provider
			value={{
				getPermissions,
				loading,
				permError,
			}}
		>
			{children}
		</PermissionContext.Provider>
	);
};

export const usePermissions = (permalink: string) => {
	const context = useContext(PermissionContext);
	if (!context) {
		throw new Error('usePermissions must be used within a PermissionProvider');
	}

	return {
		permissions: context.getPermissions(permalink),
		loading: context.loading,
		permError: context.permError,
	};
};
